import { HSpacer, IconButton, Text, VSpacer } from '@/components/DesignSystem';
import { AboutAccountRolesDialog } from "@/components/shared/AboutAccountRolesDialog";
import { CustomerRoleBadge } from '@/pages/CustomerDashboard/CustomerRoleBadge';
import {
  getRoleDescription,
} from '@/pages/CustomerDashboard/LinkedAccounts/AboutAccountRolesModalContent';
import { sortLinkedAccounts } from '@/utilities/Farmer';
import { ApiUser } from '@api/interfaces';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import Notifications from '@mui/icons-material/Notifications';
import NotificationsOff from '@mui/icons-material/NotificationsOff';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AccountGroupRole, State, StateAbbreviations } from '@shared/enums';
import { formatPhoneNumber } from '@shared/utilities';
import { getAccountName } from '@shared/utilities/UserUtilities';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface LinkedAccountsModalProps {
  accounts: ApiUser[],
  brandColor?: string,
  farmer: ApiUser,
  onClose: () => void,
}

export const LinkedAccountsModal = ({
  accounts,
  brandColor,
  farmer,
  onClose,
}: LinkedAccountsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showAboutRolesModal, setShowAboutRolesModal] = React.useState(false);

  const sortedAccounts = useMemo(() => (
    sortLinkedAccounts(accounts) as ApiUser[]
  ), [accounts]);

  const partnerAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Partner
  ));
  const independentPartnerAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.IndependentPartner
  ));
  const primaryAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Primary
  ));
  const secondaryAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Secondary
  ));

  const brandColorBg = brandColor ?? theme.palette.primary.main;

  const hasPartner = !!partnerAccounts.length;
  const hasIndependentPartner = !!independentPartnerAccounts.length;
  const hasPrimaryAndSecondary = !!primaryAccounts.length && !!secondaryAccounts.length;
  const accountTypeCount = [hasPrimaryAndSecondary, hasPartner, hasIndependentPartner]
    .filter(Boolean).length;
  const isMixedGroup = accountTypeCount >= 2;

  const AccountGroupSection = ({
    accountGroup,
    subtitle,
    title,
  }: {
    accountGroup: ApiUser[],
    subtitle: string,
    title: string,
  }) => {
    return (
      <Box
        sx={{
          backgroundColor: '#F8F8F8',
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: '8px',
          mx: '16px',
          mb: '16px',
        }}
      >
        <Box px="16px" py="12px">
          <Text category="title-medium" fontWeight={500}>
            {title}
          </Text>
          <VSpacer size="1" />
          <Text category="body-small" fontWeight={400} sx={{ color: '#525252' }}>
            {subtitle}
          </Text>
          <Stack>
            {accountGroup.map((account) => (
              <LinkedAccountCard
                account={account}
                key={account.id}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    );
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
          },
        }}
        fullScreen={isMobile}
        maxWidth="sm"
        onClose={onClose}
        open
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: '16px',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              color: brandColorBg,
            }}
            testID="close-button"
          >
            {isMobile ? (
              <ArrowBack sx={{ height: 24, width: 24 }} />
            ) : (
              <Close sx={{ height: 24, width: 24 }} />
            )}
          </IconButton>
          <Text
            category="label-large"
            onClick={() => setShowAboutRolesModal(true)}
            sx={{ color: brandColorBg, cursor: 'pointer' }}
            testID="about-roles-button"
          >
            {t($ => $.customerDashboard.howItWorks.buttons.aboutAccountRoles)}
          </Text>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box pt="8px" px="16px">
            <Text category="headline-medium" fontWeight={400}>
              {t($ => $.customerDashboard.linkedAccounts.title)}
            </Text>
            <VSpacer size="2" />
            <Text category="label-large" sx={{ color: '#525252' }}>
              {t($ => $.customerDashboard.linkedAccounts.account, { name: getAccountName(farmer) })}
            </Text>
          </Box>
          <VSpacer size="7" />
          {!!partnerAccounts.length && (
            <AccountGroupSection
              accountGroup={partnerAccounts}
              subtitle={getRoleDescription(AccountGroupRole.Partner, isMixedGroup, t)}
              title={t($ => $.customerDashboard.linkedAccounts.groups.partner)}
            />
          )}
          {!!independentPartnerAccounts.length && (
            <AccountGroupSection
              accountGroup={independentPartnerAccounts}
              subtitle={getRoleDescription(AccountGroupRole.IndependentPartner, isMixedGroup, t)}
              title={t($ => $.customerDashboard.linkedAccounts.groups.independentPartner)}
            />
          )}
          {!!primaryAccounts.length && (
            <AccountGroupSection
              accountGroup={primaryAccounts}
              subtitle={getRoleDescription(AccountGroupRole.Primary, isMixedGroup, t)}
              title={t($ => $.customerDashboard.linkedAccounts.groups.primary)}
            />
          )}
          {!!secondaryAccounts.length && (
            <AccountGroupSection
              accountGroup={secondaryAccounts}
              subtitle={getRoleDescription(AccountGroupRole.Secondary, isMixedGroup, t)}
              title={t($ => $.customerDashboard.linkedAccounts.groups.secondary)}
            />
          )}
        </DialogContent>
      </Dialog>
      {showAboutRolesModal && farmer.accountGroupRole && (
        <AboutAccountRolesDialog
          brandColor={brandColor}
          customerRole={farmer?.accountGroupRole}
          linkedAccounts={accounts}
          onClose={() => setShowAboutRolesModal(false)}
        />
      )}
    </>
  );
};

interface LinkedAccountCardProps {
  account: ApiUser,
}

const LinkedAccountCard = ({
  account,
}: LinkedAccountCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = account;
  const accountName = getAccountName(user);
  const formattedLocation = user.city && user.state
    ? `${user.city}, ${StateAbbreviations[user.state as State] ?? user.state}`
    : null;
  const formattedPhone = user.telephone
    ? formatPhoneNumber(user.telephone, 'paren')
    : null;

  return (
    <Stack p="16px" spacing="4px" sx={{
      backgroundColor: theme.palette.common.white,
      borderRadius: '8px',
      marginTop: '12px',
    }}>
      {!!user.businessName && user.businessName !== accountName && (
        <Text
          category="overline"
          sx={{ color: '#525252', fontSize: '10px', letterSpacing: '1px', wordBreak: 'break-all' }}
        >
          {user.businessName}
        </Text>
      )}
      <Text category="title-large" fontWeight={400} sx={{ wordBreak: 'break-all' }}>
        {accountName}
      </Text>
      {!!(formattedLocation || formattedPhone) && (
        <Text
          category="body-medium"
          sx={{
            color: '#525252',
            overflowWrap: 'break-word',
            maxWidth: '100%',
          }}
        >
          {[formattedLocation, formattedPhone].filter(Boolean).join(' • ')}
        </Text>
      )}
      <VSpacer size="3" />
      <Stack alignItems="center" direction="row">
        {!!account.accountGroupRole && (
          <CustomerRoleBadge
            accountGroupRole={account.accountGroupRole}
            testID="linked-accounts-modal-role-badge"
          />
        )}
        {account.accountGroupRole === AccountGroupRole.Partner && (
          <>
            <HSpacer size="3" />
            {account.sendNotifications ? (
              <Notifications
                sx={{
                  color: '#525252',
                  height: '16px',
                  width: '16px',
                }}
              />
            ) : (
              <NotificationsOff
                sx={{
                  color: '#525252',
                  height: '16px',
                  width: '16px',
                }}
              />
            )}
            <HSpacer size="2" />
            <Text
              category="overline"
              sx={{ color: '#525252', fontSize: '11px', letterSpacing: '1px' }}
            >
              {account.sendNotifications
                ? t($ => $.customerDashboard.linkedAccounts.notificationsOn)
                : t($ => $.customerDashboard.linkedAccounts.notificationsOff)}
            </Text>
          </>
        )}
      </Stack>
    </Stack>
  );
};
